<template>
  <scoped>
    <VerticalNavigator>
      <Breadcrumb
        v-if="order"
        :links="[{
          title: 'RutSolve',
          routeName: 'index'
        }, {
          title: 'Purchase Orders',
          routeName: 'purchase_order_overview'
        }]"
        :title="`${order.type} ${order.number}`"
      />
      <h1>
        Edit Purchase Order
      </h1>
      <OrderEditor v-model:value="order" />
      <h2>
        Supplier
      </h2>
      <TextInput
        v-if="order && order.supplier"
        readOnly
        :value="order.supplier.name"
      />
      <h2>
        Order Lines
      </h2>
      <OrderLineList
        v-if="order && order.orderLines"
        v-model:items="order.orderLines.records"
        v-model:selectedIndex="selectedOrderLineIndex"
        @select="selectedOrderLine = order.orderLines.records[selectedOrderLineIndex]"
      />
      <ContentModal v-if="selectedOrderLine">
        <Button
          title="Deliverable"
          @click="pickDeliverable = true"
        />
        <ContentModal v-if="pickDeliverable">
          <DeliverablePicker
            v-model:value="selectedOrderLine.deliverable"
            @select="selectDeliverable()"
          />
        </ContentModal>
        <OrderLineEditor v-model:value="selectedOrderLine" />
        <br>
        <Button
          title="Apply"
          @click="selectedOrderLine = null"
        />
      </ContentModal>
      <h2>
        Contact Details
      </h2>
      <ContactDetailsList
        v-if="order && order.contactDetails"
        v-model:items="order.contactDetails.records"
        v-model:selectedIndex="selectedContactDetailsIndex"
        @select="selectedContactDetails = order.contactDetails.records[selectedContactDetailsIndex]"
      />
      <ContentModal v-if="selectedContactDetails">
        <ContactDetailsEditor v-model:value="selectedContactDetails" />
        <br>
        <Button
          title="Apply"
          @click="selectedContactDetails = null"
        />
      </ContentModal>
      <h2>
        Files
      </h2>
      <FileList
        v-if="order && order.files"
        v-model:items="order.files.records"
        v-model:selectedIndex="selectedFileIndex"
        @select="selectedFile = order.files.records[selectedFileIndex]"
      />
      <ContentModal v-if="selectedFile">
        <FileEditor v-model:value="selectedFile" />
        <br>
        <Button
          title="Apply"
          @click="selectedFile = null"
        />
      </ContentModal>
      <Button
        title="Remove"
        @click="confirmRemove = true"
      />
      <Button
        title="Cancel"
        @click="ordersRedirect()"
      />
      <Button
        title="Save"
        @click="save()"
      />
      <ConfirmationModal
        v-model:display="confirmRemove"
        :callback="remove"
        message="This action will remove selected order."
        title="Remove Order"
      />
    </VerticalNavigator>
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import TextInput from "@/components/inputs/text.vue"
  import VerticalNavigator from "@/layouts/vertical_navigator.vue"
  import Breadcrumb from "@/components/breadcrumb.vue"
  import OrderEditor from "@/components/editors/purchase_order.vue"
  import {Order as OrderClient} from "@/endpoints/rutsolve/gql/resolvers/order"
  import {onBeforeMount, ref} from "vue"
  import {
    Order,
    ContactDetails,
    OrderLine,
    File
  } from "@/endpoints/rutsolve/gql/generated/graphql"
  import Button from "@/components/button.vue"
  import router from "@/router"
  import ErrorModal from "@/components/modals/error.vue"
  import {OrderLine as OrderLineClient} from "@/endpoints/rutsolve/gql/resolvers/order_line"
  import {ContactDetails as ContactDetailsClient} from "@/endpoints/rutsolve/gql/resolvers/contact_details"
  import {File as FileClient} from "@/endpoints/rutsolve/gql/resolvers/file"
  import DeliverablePicker from "@/components/selectors/deliverable.vue"
  import OrderLineEditor from "@/components/editors/order_line.vue"
  import FileEditor from "@/components/editors/file.vue"
  import ContentModal from "@/components/modals/content.vue"
  import OrderLineList from "@/components/lists/order_line.vue"
  import FileList from "@/components/lists/file.vue"
  import ContactDetailsEditor from "@/components/editors/contact_details.vue"
  import ContactDetailsList from "@/components/lists/contact_details.vue"
  import {useRoute} from "vue-router"
  import ConfirmationModal from "@/components/modals/confirmation.vue"
  
  const order = ref<Order>(null)
  const error = ref<Error>(null)
  const orderClient = new OrderClient()
  const orderLineClient = new OrderLineClient()
  const fileClient = new FileClient()
  const contactDetailsClient = new ContactDetailsClient()
  const selectedContactDetails = ref<ContactDetails>(null)
  const selectedContactDetailsIndex = ref<number>(null)
  const selectedFile = ref<File>(null)
  const selectedFileIndex = ref<number>(null)
  const selectedOrderLine = ref<OrderLine>(null)
  const selectedOrderLineIndex = ref<number>(null)
  const pickDeliverable = ref<boolean>(null)
  const confirmRemove = ref<boolean>(false)
  
  onBeforeMount(() => {
    read()
  })

  async function read() {
    try {
      const orderID = useRoute().params.orderID.toString()
      order.value = await orderClient.order(orderID)
      if (!order.value.contactDetails || !order.value.contactDetails.records) {
        order.value.contactDetails = {
          records: []
        }
      }
      if (!order.value.files || !order.value.files.records) {
        order.value.files = {
          records: []
        }
      }
      if (!order.value.orderLines || !order.value.orderLines.records) {
        order.value.orderLines = {
          records: []
        }
      }
    } catch(e) {
      error.value = e
    }
  }
  
  async function save() {
    try {
      await orderClient.update(order.value)
      for (const contactDetails of order.value.contactDetails.records) {
        contactDetails.relationID = order.value.id
        contactDetails.relationType = "ORDER"
        if (contactDetails.deletedAt) {
          await contactDetailsClient.remove(contactDetails.id)
        } else if(contactDetails.id) {
          await contactDetailsClient.update(contactDetails)
        } else {
          await contactDetailsClient.create(contactDetails)
        }
      }
      for (const file of order.value.files.records) {
        file.relationID = order.value.id
        file.relationType = "ORDER"
        if (file.deletedAt) {
          await fileClient.remove(file.id)
        } else if(file.id) {
          await fileClient.update(file)
        } else {
          await fileClient.create(file)
        }
      }
      for (const orderLine of order.value.orderLines.records) {
        orderLine.orderID = order.value.id
        if (orderLine.deletedAt) {
          await orderLineClient.remove(orderLine.id)
        } else if(orderLine.id) {
          await orderLineClient.update(orderLine)
        } else {
          await orderLineClient.create(orderLine)
        }
      }
      ordersRedirect()
    } catch(e) {
      error.value = e
    }
  }

  async function remove() {
    try {
      await orderClient.remove(order.value.id)
      ordersRedirect()
    } catch(e) {
      error.value = e
    }
  }
  
  function ordersRedirect() {
    router.push({
      name: "purchase_order_overview",
    })
  }
  
  function selectDeliverable() {
    selectedOrderLine.value.amount = 1
    selectedOrderLine.value.deliverableID = selectedOrderLine.value.deliverable.id
    selectedOrderLine.value.description = selectedOrderLine.value.deliverable.name
    selectedOrderLine.value.unit = selectedOrderLine.value.deliverable.unit
    selectedOrderLine.value.unitPrice = selectedOrderLine.value.deliverable.unitPrice
    selectedOrderLine.value.vatRate = selectedOrderLine.value.deliverable.vatRate
    pickDeliverable.value = false
  }
</script>
